import { useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  Badge,
  Banner,
  Box,
  Button,
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  Flex,
  Link,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderTitle,
  Tooltip,
} from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { PersonAttributesEditor, type CustomerAttributes, type PersonAttributesEnvironment } from '@/features/people';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useAnalytics } from '@/services/analytics';
import { customerName } from '@/services/db/customer/display';
import { getEnvironmentQueryOptions } from '@/services/db/environment/queries';
import {
  hasServerError,
  PersonNotCreatedError,
  useCreatePersonFlow,
  useNewPersonForm,
  useUnsavedChangesPrompt,
} from './hooks';

const SAVE_TOOLTIP = 'Fix errors before saving.';
const READ_ONLY_TOOLTIP = "You don't have write permissions in this account.";
const HEADER_DESCRIPTION_STYLE = { width: '25%' };

type SaveActionsProps = {
  isSaving: boolean;
  isSaveDisabled: boolean;
  canEdit: boolean;
  onSave: () => void;
  onDiscard: () => void;
};

function SaveActions({ isSaving, isSaveDisabled, canEdit, onSave, onDiscard }: SaveActionsProps) {
  // A viewer without write permission cannot fix anything by editing the form,
  // so the tooltip names the permission rather than pointing at the values.
  const tooltip = canEdit ? SAVE_TOOLTIP : READ_ONLY_TOOLTIP;

  return (
    <PageHeaderActions>
      <Button
        variant="secondary"
        isCritical
        isDisabled={isSaving}
        onClick={onDiscard}
        data-test="discard-changes-button"
      >
        Discard changes
      </Button>
      <Tooltip content={tooltip} isDisabled={!isSaveDisabled || isSaving} isCustom>
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            variant="primary"
            isDisabled={isSaveDisabled}
            isLoading={isSaving}
            onClick={onSave}
            data-test="save-changes-button"
          >
            Save changes
          </Button>
        )}
      </Tooltip>
    </PageHeaderActions>
  );
}

type SuppressedIdentifierBannerProps = {
  suppressionsHref: string;
};

function SuppressedIdentifierBanner({ suppressionsHref }: SuppressedIdentifierBannerProps) {
  return (
    <Banner
      variant="critical"
      description={
        <>
          This person cannot be added because this id/email has been suppressed.{' '}
          <Link href={suppressionsHref} variant="secondary">
            Unsuppress people
          </Link>
        </>
      }
      data-test="suppressed-identifier-banner"
    />
  );
}

type NewPersonSummaryProps = {
  /** The attributes of the last save attempt, if any. */
  attributes: CustomerAttributes | null;
};

/** The name, contact and status block above the editor; a person is subscribed until told otherwise. */
function NewPersonSummary({ attributes }: NewPersonSummaryProps) {
  const name = customerName(attributes ?? {}) ?? '(New Person)';
  const email = attributes?.email ?? '';

  return (
    <Flex data-test="new-person-summary">
      <DescriptionList style={HEADER_DESCRIPTION_STYLE}>
        <DescriptionListTerm className="fs-mask">{name}</DescriptionListTerm>
        <DescriptionListDetails color="subtle" className="fs-mask">
          {email}
        </DescriptionListDetails>
      </DescriptionList>
      <DescriptionList>
        <DescriptionListTerm>Status</DescriptionListTerm>
        <DescriptionListDetails>
          <Badge color="green">Subscribed</Badge>
        </DescriptionListDetails>
      </DescriptionList>
    </Flex>
  );
}

type NewPersonFormProps = {
  workspaceId: number;
  environment: PersonAttributesEnvironment;
  pageTitle: string;
};

function NewPersonForm({ workspaceId, environment, pageTitle }: NewPersonFormProps) {
  const router = useRouter();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('journeys/people/edit');

  const form = useNewPersonForm(environment);
  const { isDirty, isValid, errors } = form.formState;

  const hasUnsavedChangesRef = useRef(false);
  hasUnsavedChangesRef.current = isDirty;

  const flow = useCreatePersonFlow({ workspaceId, environment, form, hasUnsavedChangesRef });
  const isSaveDisabled = flow.isSaving || !isValid || hasServerError(errors) || !canEdit;

  // The leave prompt navigates only when its `onSave` resolves, so a create the
  // server rejected has to reject here: the prompt then closes onto the form
  // with the errors it just rendered, instead of leaving and losing them.
  const saveAndStay = useCallback(async () => {
    const didCreate = await flow.submit({ shouldNavigate: false });
    if (!didCreate) {
      throw new PersonNotCreatedError();
    }
  }, [flow]);
  useUnsavedChangesPrompt({ hasUnsavedChangesRef, form, isSaveDisabled, saveAndStay });

  const suppressionsHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/suppressions',
    params: { workspaceId },
  }).href;

  return (
    <Page>
      <PageHeader>
        <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
        {isDirty ? (
          <SaveActions
            isSaving={flow.isSaving}
            isSaveDisabled={isSaveDisabled}
            canEdit={canEdit}
            onSave={() => void flow.submit()}
            onDiscard={flow.discard}
          />
        ) : null}
      </PageHeader>
      <PageBody>
        {flow.suppressionErrorAttribute != null ? (
          <Box mb="200">
            <SuppressedIdentifierBanner suppressionsHref={suppressionsHref} />
          </Box>
        ) : null}
        <NewPersonSummary attributes={flow.submittedAttributes} />
        <Box mt="300">
          <PersonAttributesEditor form={form} environment={environment} canEdit={canEdit} isSaving={flow.isSaving} />
        </Box>
      </PageBody>
    </Page>
  );
}

/**
 * The add-person page. The environment (identifier config, attribute catalog)
 * gates the form the way the route's model did: nothing renders until it has
 * loaded, and a failed load goes to the route error boundary.
 */
export default function PeopleNewPage() {
  const pageTitle = 'Add a Person';
  const { track } = useAnalytics();
  usePageViewTracking(
    useCallback(() => {
      void track('people', 'manualPersonCreationStarted', {});
    }, [track]),
  );

  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? 0;
  const environmentQuery = useQuery({
    ...getEnvironmentQueryOptions(workspaceId),
    enabled: workspaceId > 0,
    throwOnError: true,
  });
  const environment = environmentQuery.data?.environment;

  return (
    <>
      <DocumentTitle title={pageTitle} />
      {environment == null ? (
        <LoadingPage />
      ) : (
        <NewPersonForm workspaceId={workspaceId} environment={environment} pageTitle={pageTitle} />
      )}
    </>
  );
}
